import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminInventoryApi } from '../api/operations';
import type {
  InventoryItemDto,
  InventoryMovementQueryDto,
  InventoryMovementType,
  InventoryQueryDto,
  LocationKind,
  LocationUpsertDto,
} from '../api/types';
import { inventoryMovementTypeValues, locationKindValues } from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, StatTile, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import { ConfirmDialog, Field, FormErrors, Modal, Pagination, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, money, number } from '../lib/format';
import { P } from '../lib/permissions';

/* ------------------------------------------------------------------ *
 *  Stok yonetimi.
 *
 *  Stok dogrudan set edilmez; fark (delta) veya sayim sonucu gonderilir.
 *  Boylece es zamanli iki duzeltme birbirini ezmez ve her degisiklik
 *  stok defterine yazilir.
 * ------------------------------------------------------------------ */

const TABS = [
  { key: 'stok', label: 'Stok durumu' },
  { key: 'hareketler', label: 'Stok defteri' },
  { key: 'konumlar', label: 'Konumlar' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const movementTypeLabels: Record<InventoryMovementType, string> = {
  Adjustment: 'Elle düzeltme',
  Sale: 'Satış',
  ReturnRestock: 'İade girişi',
  TransferOut: 'Transfer çıkışı',
  TransferIn: 'Transfer girişi',
  Receipt: 'Mal kabul',
  Damage: 'Hasar',
  Loss: 'Kayıp',
  Reservation: 'Rezervasyon',
  ReservationRelease: 'Rezervasyon iptali',
  Fulfillment: 'Sevkiyat',
  Cancellation: 'İptal',
  BulkImport: 'Toplu içe aktarım',
};

export function InventoryPage() {
  const [tab, setTab] = useState<TabKey>('stok');

  return (
    <div>
      <PageHeader title="Stok" description="Elde, rezerve, taahhüt ve satılabilir miktarlar" />

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-ink-200">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition ${
              tab === item.key
                ? 'border-ink-900 font-medium text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'stok' ? <StockTab /> : null}
      {tab === 'hareketler' ? <MovementsTab /> : null}
      {tab === 'konumlar' ? <LocationsTab /> : null}
    </div>
  );
}

/* ------------------------------ Stok durumu ------------------------------ */

function StockTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [query, setQuery] = useState<InventoryQueryDto>({ page: 1, pageSize: 20 });
  const [adjusting, setAdjusting] = useState<InventoryItemDto | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const inventory = useQuery({
    queryKey: ['admin', 'inventory', query],
    queryFn: () => adminInventoryApi.search(query),
    placeholderData: keepPreviousData,
  });

  const lowStock = useQuery({
    queryKey: ['admin', 'inventory', 'low-stock'],
    queryFn: () => adminInventoryApi.lowStock(100),
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: () => adminInventoryApi.locations(),
    staleTime: 5 * 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'inventory'] });

  const columns: Column<InventoryItemDto>[] = [
    {
      key: 'product',
      header: 'Ürün',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 overflow-hidden rounded bg-ink-50">
            {row.imageUrl ? (
              <img src={row.imageUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium text-ink-900">{row.productTitle}</p>
            <p className="truncate text-xs text-ink-400">
              {row.variantTitle} · {row.sku}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'onHand',
      header: 'Elde',
      align: 'right',
      cell: (row) => number(row.totalOnHand),
    },
    {
      key: 'available',
      header: 'Satılabilir',
      align: 'right',
      cell: (row) => (
        <span
          className={
            row.totalAvailable <= 0
              ? 'font-medium text-red-600'
              : row.isLowStock
                ? 'font-medium text-amber-600'
                : 'text-ink-800'
          }
        >
          {number(row.totalAvailable)}
        </span>
      ),
    },
    {
      key: 'threshold',
      header: 'Kritik eşik',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.lowStockThreshold),
    },
    {
      key: 'cost',
      header: 'Birim maliyet',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => (row.unitCost ? money(row.unitCost) : <span className="text-ink-300">—</span>),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="btn-ghost btn-sm"
            onClick={() => setExpanded((current) => (current === row.id ? null : row.id))}
          >
            {expanded === row.id ? 'Gizle' : 'Konumlar'}
          </button>
          {can(P.inventory.adjust) ? (
            <button type="button" className="btn-secondary btn-sm" onClick={() => setAdjusting(row)}>
              Düzelt
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  const rows = inventory.data?.items ?? [];
  const expandedItem = rows.find((row) => row.id === expanded) ?? null;

  return (
    <div>
      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <StatTile
          label="İzlenen varyant"
          value={number(inventory.data?.totalCount ?? 0)}
        />
        <StatTile
          label="Kritik seviyede"
          value={number(lowStock.data?.filter((item) => item.totalAvailable > 0).length ?? 0)}
          tone="negative"
        />
        <StatTile
          label="Tükenen"
          value={number(lowStock.data?.filter((item) => item.totalAvailable <= 0).length ?? 0)}
          tone="negative"
        />
      </div>

      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="Ürün adı, SKU veya barkod"
        />

        <SelectFilter
          label="Konum"
          value={query.locationId ? String(query.locationId) : ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, locationId: value ? Number(value) : undefined, page: 1 }))
          }
          options={(locations.data ?? []).map((location) => ({
            value: String(location.id),
            label: location.name,
          }))}
        />

        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={query.lowStockOnly === true}
              onChange={(event) =>
                setQuery((c) => ({ ...c, lowStockOnly: event.target.checked || undefined, page: 1 }))
              }
            />
            Kritik seviyedekiler
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={query.outOfStockOnly === true}
              onChange={(event) =>
                setQuery((c) => ({
                  ...c,
                  outOfStockOnly: event.target.checked || undefined,
                  page: 1,
                }))
              }
            />
            Tükenenler
          </label>
        </div>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={inventory.isLoading}
        error={inventory.error}
        onRetry={() => void inventory.refetch()}
        emptyMessage="Stok kaydı bulunamadı."
        footer={
          inventory.data ? (
            <Pagination
              page={inventory.data.page}
              totalPages={inventory.data.totalPages}
              totalCount={inventory.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />

      {/* Konum kirilimi */}
      {expandedItem ? (
        <section className="card mt-4 p-5">
          <h3 className="mb-3 text-sm font-semibold text-ink-900">
            {expandedItem.productTitle} — konum kırılımı
          </h3>
          <div className="scroll-x">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                  <th className="pb-2 font-semibold">Konum</th>
                  <th className="pb-2 text-right font-semibold">Elde</th>
                  <th className="pb-2 text-right font-semibold">Rezerve</th>
                  <th className="pb-2 text-right font-semibold">Taahhüt</th>
                  <th className="pb-2 text-right font-semibold">Satılabilir</th>
                  <th className="pb-2 text-right font-semibold">Gelen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {expandedItem.levels.map((level) => (
                  <tr key={level.locationId}>
                    <td className="py-2 text-ink-800">{level.locationName}</td>
                    <td className="py-2 text-right">{number(level.onHand)}</td>
                    <td className="py-2 text-right text-ink-500">{number(level.reserved)}</td>
                    <td className="py-2 text-right text-ink-500">{number(level.committed)}</td>
                    <td className="py-2 text-right font-medium">{number(level.available)}</td>
                    <td className="py-2 text-right text-ink-500">{number(level.incoming)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-ink-500">
            Satılabilir = Elde − Rezerve − Taahhüt. Rezerve, sepette bekleyen; taahhüt, siparişe
            bağlanmış ama henüz sevk edilmemiş miktardır.
          </p>
        </section>
      ) : null}

      <AdjustModal
        item={adjusting}
        locations={locations.data ?? []}
        onClose={() => setAdjusting(null)}
        onDone={() => {
          invalidate();
          toast.success('Stok güncellendi.');
        }}
      />
    </div>
  );
}

function AdjustModal({
  item,
  locations,
  onClose,
  onDone,
}: {
  item: InventoryItemDto | null;
  locations: { id: number; name: string }[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<'delta' | 'count'>('delta');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [delta, setDelta] = useState(0);
  const [counted, setCounted] = useState(0);
  const [movementType, setMovementType] = useState<InventoryMovementType>('Adjustment');
  const [reason, setReason] = useState('');

  const activeLocation = locationId ?? item?.levels[0]?.locationId ?? locations[0]?.id ?? null;

  const adjust = useMutation({
    mutationFn: () =>
      mode === 'delta'
        ? adminInventoryApi.adjust({
            productVariantId: item!.productVariantId,
            locationId: activeLocation!,
            quantityDelta: delta,
            movementType,
            reason: reason || null,
          })
        : adminInventoryApi.applyCount({
            productVariantId: item!.productVariantId,
            locationId: activeLocation!,
            countedQuantity: counted,
            reason: reason || null,
          }),
    onSuccess: () => {
      onDone();
      onClose();
      setDelta(0);
      setReason('');
    },
    onError: (error) => toast.fromError(error, 'Stok güncellenemedi.'),
  });

  const currentLevel = item?.levels.find((level) => level.locationId === activeLocation);

  return (
    <Modal
      open={item !== null}
      title="Stok düzeltmesi"
      description={item ? `${item.productTitle} · ${item.sku}` : undefined}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Vazgeç
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => adjust.mutate()}
            disabled={adjust.isPending || activeLocation === null || (mode === 'delta' && delta === 0)}
          >
            {adjust.isPending ? <Spinner className="h-4 w-4" /> : null}
            Uygula
          </button>
        </>
      }
    >
      {item ? (
        <div className="space-y-4">
          <FormErrors error={adjust.error} />

          <div className="flex rounded-lg border border-ink-200 p-0.5">
            <button
              type="button"
              className={`flex-1 rounded-md px-3 py-2 text-sm ${mode === 'delta' ? 'bg-ink-900 text-white' : 'text-ink-600'}`}
              onClick={() => setMode('delta')}
            >
              Fark gir
            </button>
            <button
              type="button"
              className={`flex-1 rounded-md px-3 py-2 text-sm ${mode === 'count' ? 'bg-ink-900 text-white' : 'text-ink-600'}`}
              onClick={() => setMode('count')}
            >
              Sayım sonucu
            </button>
          </div>

          <Field label="Konum" htmlFor="adj-location" required>
            <select
              id="adj-location"
              className="field"
              value={activeLocation ?? ''}
              onChange={(event) => setLocationId(Number(event.target.value))}
            >
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>

          {currentLevel ? (
            <p className="rounded-lg bg-ink-50 px-3 py-2 text-sm text-ink-600">
              Bu konumda şu an: elde {number(currentLevel.onHand)}, satılabilir{' '}
              {number(currentLevel.available)}
            </p>
          ) : null}

          {mode === 'delta' ? (
            <>
              <Field
                label="Değişim miktarı"
                htmlFor="adj-delta"
                required
                hint="Artış için pozitif, azalış için negatif değer girin."
              >
                <input
                  id="adj-delta"
                  type="number"
                  className="field"
                  value={delta}
                  onChange={(event) => setDelta(Number(event.target.value))}
                />
              </Field>

              <Field label="Hareket türü" htmlFor="adj-type" required>
                <select
                  id="adj-type"
                  className="field"
                  value={movementType}
                  onChange={(event) =>
                    setMovementType(event.target.value as InventoryMovementType)
                  }
                >
                  {inventoryMovementTypeValues
                    .filter((value) =>
                      ['Adjustment', 'Receipt', 'Damage', 'Loss', 'TransferIn', 'TransferOut'].includes(
                        value,
                      ),
                    )
                    .map((value) => (
                      <option key={value} value={value}>
                        {movementTypeLabels[value]}
                      </option>
                    ))}
                </select>
              </Field>
            </>
          ) : (
            <Field
              label="Sayılan miktar"
              htmlFor="adj-count"
              required
              hint="Fark otomatik hesaplanır ve deftere yazılır."
            >
              <input
                id="adj-count"
                type="number"
                min={0}
                className="field"
                value={counted}
                onChange={(event) => setCounted(Number(event.target.value))}
              />
            </Field>
          )}

          <Field label="Gerekçe" htmlFor="adj-reason">
            <input
              id="adj-reason"
              className="field"
              placeholder="Depo sayımı, hasarlı ürün vb."
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        </div>
      ) : null}
    </Modal>
  );
}

/* ------------------------------ Stok defteri ------------------------------ */

function MovementsTab() {
  const [query, setQuery] = useState<InventoryMovementQueryDto>({ page: 1, pageSize: 30 });

  const movements = useQuery({
    queryKey: ['admin', 'inventory', 'movements', query],
    queryFn: () => adminInventoryApi.movements(query),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof movements.data>['items'][number]>[] = [
    {
      key: 'occurred',
      header: 'Zaman',
      cell: (row) => <span className="text-ink-600">{formatDateTime(row.occurredAt)}</span>,
    },
    {
      key: 'product',
      header: 'Ürün',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{row.productTitle}</p>
          <p className="truncate font-mono text-xs text-ink-400">{row.sku}</p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Hareket',
      cell: (row) => (
        <span className="badge bg-ink-100 text-ink-700">
          {movementTypeLabels[row.movementType] ?? row.movementType}
        </span>
      ),
    },
    {
      key: 'location',
      header: 'Konum',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-600">{row.locationName}</span>,
    },
    {
      key: 'delta',
      header: 'Fark',
      align: 'right',
      cell: (row) => (
        <span className={row.quantityDelta >= 0 ? 'text-emerald-700' : 'text-red-600'}>
          {row.quantityDelta > 0 ? '+' : ''}
          {row.quantityDelta}
        </span>
      ),
    },
    {
      key: 'after',
      header: 'Sonrası',
      align: 'right',
      cell: (row) => (
        <span className="text-ink-600">
          elde {number(row.onHandAfter)} / satılabilir {number(row.availableAfter)}
        </span>
      ),
    },
    {
      key: 'reference',
      header: 'Referans',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-xs text-ink-500">
          {row.referenceLabel ?? row.reason ?? row.note ?? '—'}
        </span>
      ),
    },
    {
      key: 'by',
      header: 'Kaydeden',
      hideOnMobile: true,
      cell: (row) => <span className="text-xs text-ink-500">{row.performedBy ?? 'Sistem'}</span>,
    },
  ];

  return (
    <div>
      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="SKU veya ürün adı"
        />

        <SelectFilter
          label="Hareket türü"
          value={query.movementType ?? ''}
          onChange={(value) =>
            setQuery((c) => ({
              ...c,
              movementType: (value || undefined) as InventoryMovementType,
              page: 1,
            }))
          }
          options={inventoryMovementTypeValues.map((value) => ({
            value,
            label: movementTypeLabels[value],
          }))}
        />

        <Field label="Başlangıç" htmlFor="mv-from">
          <input
            id="mv-from"
            type="date"
            className="field py-2 text-sm"
            value={query.from?.slice(0, 10) ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, from: event.target.value || undefined, page: 1 }))
            }
          />
        </Field>

        <Field label="Bitiş" htmlFor="mv-to">
          <input
            id="mv-to"
            type="date"
            className="field py-2 text-sm"
            value={query.to?.slice(0, 10) ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, to: event.target.value || undefined, page: 1 }))
            }
          />
        </Field>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={movements.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={movements.isLoading}
        error={movements.error}
        onRetry={() => void movements.refetch()}
        emptyMessage="Stok hareketi bulunamadı."
        footer={
          movements.data ? (
            <Pagination
              page={movements.data.page}
              totalPages={movements.data.totalPages}
              totalCount={movements.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />
    </div>
  );
}

/* -------------------------------- Konumlar -------------------------------- */

const emptyLocation: LocationUpsertDto = {
  name: '',
  code: '',
  kind: 'Warehouse',
  isActive: true,
  fulfillsOnlineOrders: true,
  supportsPickup: false,
  countsTowardsStorefrontStock: true,
  priority: 0,
  countryCode: 'TR',
};

function LocationsTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{ id: number | null; form: LocationUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: () => adminInventoryApi.locations(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'locations'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminInventoryApi.createLocation(editing!.form)
        : adminInventoryApi.updateLocation(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Konum kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminInventoryApi.deleteLocation(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Konum silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Konum silinemedi.'),
  });

  const kindLabels: Record<LocationKind, string> = {
    Warehouse: 'Depo',
    RetailStore: 'Mağaza',
    Dropshipper: 'Tedarikçi',
    Virtual: 'Sanal',
  };

  const columns: Column<NonNullable<typeof locations.data>[number]>[] = [
    {
      key: 'name',
      header: 'Konum',
      cell: (row) => (
        <div>
          <p className="font-medium text-ink-900">{row.name}</p>
          <p className="font-mono text-xs text-ink-400">{row.code}</p>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Tür',
      cell: (row) => <span className="badge bg-ink-100 text-ink-700">{kindLabels[row.kind]}</span>,
    },
    {
      key: 'address',
      header: 'Adres',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-ink-600">
          {[row.district, row.city].filter(Boolean).join(' / ') || '—'}
        </span>
      ),
    },
    {
      key: 'flags',
      header: 'Özellikler',
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.fulfillsOnlineOrders ? (
            <span className="badge bg-brand-50 text-brand-800">Sevkiyat</span>
          ) : null}
          {row.supportsPickup ? (
            <span className="badge bg-ink-100 text-ink-700">Mağazadan teslim</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Öncelik',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.priority),
    },
    {
      key: 'status',
      header: 'Durum',
      align: 'center',
      cell: (row) =>
        row.isActive ? (
          <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
        ) : (
          <span className="badge bg-ink-100 text-ink-600">Pasif</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.inventory.locations) ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() =>
                setEditing({
                  id: row.id,
                  form: {
                    name: row.name,
                    code: row.code,
                    kind: row.kind,
                    isActive: row.isActive,
                    fulfillsOnlineOrders: row.fulfillsOnlineOrders,
                    supportsPickup: row.supportsPickup,
                    countsTowardsStorefrontStock: true,
                    priority: row.priority,
                    addressLine1: row.addressLine1,
                    district: row.district,
                    city: row.city,
                    postalCode: row.postalCode,
                    countryCode: row.countryCode,
                    phone: row.phone,
                    latitude: row.latitude,
                    longitude: row.longitude,
                  },
                })
              }
            >
              Düzenle
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setDeleteId(row.id)}
            >
              Sil
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      {can(P.inventory.locations) ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditing({ id: null, form: emptyLocation })}
          >
            Yeni konum
          </button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={locations.data ?? []}
        rowKey={(row) => row.id}
        isLoading={locations.isLoading}
        error={locations.error}
        onRetry={() => void locations.refetch()}
        emptyMessage="Konum tanımlanmamış."
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni konum' : 'Konumu düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name || !editing?.form.code}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ad" htmlFor="loc-name" required>
                <input
                  id="loc-name"
                  className="field"
                  value={editing.form.name}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, name: event.target.value } })
                  }
                />
              </Field>

              <Field label="Kod" htmlFor="loc-code" required hint="Benzersiz kısa kod (DEPO-1).">
                <input
                  id="loc-code"
                  className="field font-mono text-sm"
                  value={editing.form.code}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, code: event.target.value } })
                  }
                />
              </Field>

              <Field label="Tür" htmlFor="loc-kind">
                <select
                  id="loc-kind"
                  className="field"
                  value={editing.form.kind}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && { ...c, form: { ...c.form, kind: event.target.value as LocationKind } },
                    )
                  }
                >
                  {locationKindValues.map((value) => (
                    <option key={value} value={value}>
                      {kindLabels[value]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Öncelik" htmlFor="loc-priority" hint="Küçük değer önce denenir.">
                <input
                  id="loc-priority"
                  type="number"
                  className="field"
                  value={editing.form.priority}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, priority: Number(event.target.value) } },
                    )
                  }
                />
              </Field>

              <Field label="Adres" htmlFor="loc-address" className="sm:col-span-2">
                <input
                  id="loc-address"
                  className="field"
                  value={editing.form.addressLine1 ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, addressLine1: event.target.value } },
                    )
                  }
                />
              </Field>

              <Field label="İlçe" htmlFor="loc-district">
                <input
                  id="loc-district"
                  className="field"
                  value={editing.form.district ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, district: event.target.value } },
                    )
                  }
                />
              </Field>

              <Field label="İl" htmlFor="loc-city">
                <input
                  id="loc-city"
                  className="field"
                  value={editing.form.city ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, city: event.target.value } })
                  }
                />
              </Field>

              <Field label="Telefon" htmlFor="loc-phone">
                <input
                  id="loc-phone"
                  type="tel"
                  className="field"
                  value={editing.form.phone ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, phone: event.target.value } })
                  }
                />
              </Field>
            </div>

            <div className="space-y-2">
              {(
                [
                  ['isActive', 'Aktif'],
                  ['fulfillsOnlineOrders', 'Online siparişleri karşılar'],
                  ['supportsPickup', 'Mağazadan teslim alma'],
                  ['countsTowardsStorefrontStock', 'Vitrin stoğuna dahil'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600"
                    checked={editing.form[key]}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, [key]: event.target.checked } },
                      )
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Konumu sil"
        message="Bu konumda stok varsa silme işlemi reddedilir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}
